import React, { useState, useEffect } from 'react';
import {
    Calendar,
    Users,
    UserCheck,
    GraduationCap,
    ChevronLeft,
    ChevronRight,
    CheckCircle,
    XCircle,
    AlertCircle,
    Clock,
    Filter,
    Download,
    Save,
    Search,
    ChevronDown
} from 'lucide-react';
import { collection, getDocs, doc, setDoc, getDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import { Staff, Teacher } from '../../types';
import { AttendanceRecord } from '../../types/attendance';
import { SchoolTime } from '../../types/schedule';
import { getSchoolTimes } from '../../utils/schedule';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';

interface BulkAttendanceManagerProps {
    onBack: () => void;
}

type EmployeeType = 'staff' | 'teacher';
type AttendanceStatus = 'present' | 'absent' | 'leave' | 'holiday' | null;

interface EmployeeWithAttendance {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth: Date;
    gender: 'male' | 'female' | 'other';
    address: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
    };
    emergencyContact: {
        name: string;
        relationship: string;
        phone: string;
        email: string;
    };
    bankDetails: {
        accountNumber: string;
        bankName: string;
        ifscCode: string;
        accountHolderName: string;
    };
    createdAt: Date;
    updatedAt: Date;
    type: EmployeeType;
    attendance: Record<string, AttendanceStatus>;
    // Staff-specific fields
    staffId?: string;
    qualification?: {
        education: string;
        certifications: string[];
    };
    experience?: {
        totalYears: number;
        previousEmployers: Array<{
            companyName: string;
            position: string;
            duration: string;
        }>;
    };
    currentPosition?: {
        department: string;
        designation: string;
        joiningDate: Date;
        salary: number;
        status: 'active' | 'inactive' | 'resigned';
    };
    authorities?: {
        [key: string]: boolean;
    };
    role?: 'admin' | 'hr' | 'accountant' | 'teacher' | 'student' | 'staff' | 'principal' | 'clerk';
    // Teacher-specific fields
    teacherId?: string;
    subjects?: string[];
    classes?: string[];
    periods?: Array<{
        classId: string;
        className: string;
        dayOfWeek: number;
        endTime: string;
        periodId: string;
        startTime: string;
        status: string;
        subject: string;
    }>;
}

const BulkAttendanceManager: React.FC<BulkAttendanceManagerProps> = ({ onBack }) => {
    const { showSuccess, showError } = useToast();

    // State
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedEmployeeType, setSelectedEmployeeType] = useState<EmployeeType>('staff');
    const [employees, setEmployees] = useState<EmployeeWithAttendance[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [bulkStatus, setBulkStatus] = useState<AttendanceStatus>(null);
    const [targetEmployee, setTargetEmployee] = useState<EmployeeWithAttendance | null>(null);
    const [targetDate, setTargetDate] = useState<string>('');

    // Search and Pagination state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // School time configuration
    const [schoolTimes, setSchoolTimes] = useState<SchoolTime[]>([]);
    const [calendarConfig, setCalendarConfig] = useState<Record<string, any>>({});

    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    // Check if a day is a holiday based on school time configuration and custom holidays
    const isHoliday = (date: Date): boolean => {
        // Check for custom holidays first
        const dayStr = date.getDate().toString();

        if (calendarConfig[dayStr] && calendarConfig[dayStr].isHoliday) {
            return true;
        }

        // Check for school holidays
        if (schoolTimes.length === 0) return false;

        const activeSchoolTime = schoolTimes.find(st => st.isActive);
        if (!activeSchoolTime) return false;

        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        const dayConfig = activeSchoolTime.days.find(day => day.day === dayName);

        return dayConfig ? !dayConfig.isActive : false;
    };

    // Generate dates for current month
    const generateMonthDates = () => {
        const year = currentYear;
        const month = currentMonth;
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const today = new Date();

        const dates = [];
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            // Use local date formatting instead of toISOString() to avoid timezone issues
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isFuture = date > today;
            const isHolidayDay = isHoliday(date);
            dates.push({
                date,
                dateStr,
                day,
                isFuture,
                isToday: date.toDateString() === today.toDateString(),
                isHoliday: isHolidayDay
            });
        }
        return dates;
    };

    const monthDates = generateMonthDates();

    useEffect(() => {
        fetchEmployees();
        fetchSchoolTimes();
        fetchCalendarConfig();
    }, [selectedEmployeeType]);

    useEffect(() => {
        if (employees.length > 0) {
            fetchAttendanceData();
        }
        fetchCalendarConfig();
    }, [employees, currentYear, currentMonth]);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            let employeesData: any[] = [];

            if (selectedEmployeeType === 'staff') {
                employeesData = await fetchStaff();
            } else {
                employeesData = await fetchTeachers();
            }

            const employeesWithType = employeesData.map(emp => ({
                ...emp,
                type: selectedEmployeeType,
                attendance: {} as Record<string, AttendanceStatus>
            }));

            setEmployees(employeesWithType);
        } catch (error) {
            console.error('Error fetching employees:', error);
            showError('Error', 'Failed to fetch employees');
        } finally {
            setLoading(false);
        }
    };

    const fetchStaff = async (): Promise<Staff[]> => {
        try {
            const staffRef = collection(db, 'staff');
            const q = query(staffRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dateOfBirth: doc.data().dateOfBirth?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
                currentPosition: {
                    ...doc.data().currentPosition,
                    joiningDate: doc.data().currentPosition?.joiningDate?.toDate(),
                },
            })) as Staff[];
        } catch (error) {
            console.error('Error fetching staff:', error);
            return [];
        }
    };

    const fetchTeachers = async (): Promise<Teacher[]> => {
        try {
            const teachersRef = collection(db, 'teachers');
            const q = query(teachersRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dateOfBirth: doc.data().dateOfBirth?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
                currentPosition: {
                    ...doc.data().currentPosition,
                    joiningDate: doc.data().currentPosition?.joiningDate?.toDate(),
                },
            })) as Teacher[];
        } catch (error) {
            console.error('Error fetching teachers:', error);
            return [];
        }
    };

    const fetchSchoolTimes = async () => {
        try {
            const data = await getSchoolTimes();
            setSchoolTimes(data);
        } catch (error) {
            console.error('Error fetching school times:', error);
            showError('Error', 'Failed to fetch school time configuration');
        }
    };

    const fetchCalendarConfig = async () => {
        try {
            const year = currentYear.toString();
            const monthName = getMonthName(currentMonth);
            const configRef = doc(db, 'calendarConfig', `${year}_${monthName}`);
            const configSnap = await getDoc(configRef);

            if (configSnap.exists()) {
                setCalendarConfig(configSnap.data());
            } else {
                setCalendarConfig({});
            }
        } catch (error) {
            console.error('Error fetching calendar config:', error);
            showError('Error', 'Failed to fetch calendar configuration');
        }
    };

    const fetchAttendanceData = async () => {
        try {
            const year = currentYear.toString();
            const monthName = getMonthName(currentMonth);

            const updatedEmployees = await Promise.all(
                employees.map(async (employee) => {
                    const employeeId = employee.type === 'staff' ? employee.staffId : employee.teacherId;
                    if (!employeeId) return { ...employee, attendance: {} };

                    // Reference: attendance/{type}/{employeeId}/{yearMonth}
                    const yearMonth = `${year}_${monthName}`;
                    const monthRef = doc(db, 'attendance', employee.type, employeeId, yearMonth);
                    const monthSnap = await getDoc(monthRef);

                    let attendance: Record<string, AttendanceStatus> = {};

                    if (monthSnap.exists()) {
                        const monthData = monthSnap.data();

                        // Process each day in the month
                        Object.keys(monthData).forEach(dayStr => {
                            const dayData = monthData[dayStr];
                            if (dayData && typeof dayData === 'object') {
                                // Extract status from the day data
                                const status = dayData.P ? 'present' : dayData.A ? 'absent' : dayData.L ? 'leave' : null;
                                if (status) {
                                    // Create proper date string: YYYY-MM-DD
                                    const dateStr = `${year}-${String(currentMonth + 1).padStart(2, '0')}-${dayStr.padStart(2, '0')}`;
                                    attendance[dateStr] = status;
                                }
                            }
                        });
                    }

                    return { ...employee, attendance };
                })
            );

            setEmployees(updatedEmployees);
        } catch (error) {
            console.error('Error fetching attendance data:', error);
            showError('Error', 'Failed to fetch attendance data');
        }
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        const newDate = new Date(currentDate);
        if (direction === 'prev') {
            newDate.setMonth(newDate.getMonth() - 1);
        } else {
            newDate.setMonth(newDate.getMonth() + 1);
        }
        setCurrentDate(newDate);
    };

    const handleDateClick = (employee: EmployeeWithAttendance, dateStr: string) => {
        const date = new Date(dateStr);
        const today = new Date();

        if (date > today) {
            showError('Error', 'Cannot mark attendance for future dates');
            return;
        }

        if (isHoliday(date)) {
            // Check if there's already holiday work for this date
            const existingStatus = employee.attendance[dateStr];
            if (existingStatus === 'holiday') {
                // Already marked as holiday work, allow normal editing
                setTargetEmployee(employee);
                setTargetDate(dateStr);
                setBulkStatus('present'); // Default to present for holiday work
                setShowStatusModal(true);
            } else {
                showError('Error', 'Cannot mark attendance on holidays. Use Calendar Attendance view to mark holiday work.');
                return;
            }
            return;
        }

        setTargetEmployee(employee);
        setTargetDate(dateStr);
        setBulkStatus(employee.attendance[dateStr] || null);
        setShowStatusModal(true);
    };

    const handleBulkStatusSave = async () => {
        if (!targetEmployee || !targetDate || bulkStatus === null) return;

        setSaving(true);
        try {
            const employeeId = targetEmployee.type === 'staff' ? targetEmployee.staffId : targetEmployee.teacherId;
            if (!employeeId) {
                showError('Error', 'Employee ID not found');
                return;
            }

            const year = currentYear.toString();
            const monthName = getMonthName(currentMonth);
            const date = new Date(targetDate);
            const day = date.getDate().toString();

            // Reference: attendance/{type}/{employeeId}/{yearMonth}
            const yearMonth = `${year}_${monthName}`;
            const monthRef = doc(db, 'attendance', targetEmployee.type, employeeId, yearMonth);
            const monthSnap = await getDoc(monthRef);

            let monthData: any = {};
            if (monthSnap.exists()) {
                monthData = monthSnap.data();
            }

            // Create day data based on status
            let dayData: any = {};
            switch (bulkStatus) {
                case 'present':
                    dayData = {
                        P: true,
                        checkIn_time: '09:00', // Default check-in time
                        checkout_time: '17:00' // Default check-out time
                    };
                    break;
                case 'absent':
                    dayData = { A: true };
                    break;
                case 'leave':
                    dayData = {
                        L: true,
                        reason: 'Personal leave' // Default reason
                    };
                    break;
            }

            // Update the specific day
            monthData[day] = dayData;

            // Save to Firestore
            await setDoc(monthRef, monthData, { merge: true });

            // Update local state
            setEmployees(prev => prev.map(emp =>
                emp.id === targetEmployee.id
                    ? { ...emp, attendance: { ...emp.attendance, [targetDate]: bulkStatus } }
                    : emp
            ));

            showSuccess('Success', 'Attendance saved successfully!');
            setShowStatusModal(false);
            setTargetEmployee(null);
            setTargetDate('');
            setBulkStatus(null);
        } catch (error) {
            console.error('Error saving attendance:', error);
            showError('Error', 'Failed to save attendance');
        } finally {
            setSaving(false);
        }
    };

    const getStatusIcon = (status: AttendanceStatus) => {
        switch (status) {
            case 'present':
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            case 'absent':
                return <XCircle className="h-4 w-4 text-red-600" />;
            case 'leave':
                return <AlertCircle className="h-4 w-4 text-blue-600" />;
            case 'holiday':
                return <span className="text-orange-600 font-bold text-sm">H</span>;
            default:
                return <span className="text-gray-400">-</span>;
        }
    };

    const getStatusColor = (status: AttendanceStatus) => {
        switch (status) {
            case 'present':
                return 'bg-green-100 text-green-800';
            case 'absent':
                return 'bg-red-100 text-red-800';
            case 'leave':
                return 'bg-blue-100 text-blue-800';
            case 'holiday':
                return 'bg-orange-100 text-orange-800';
            default:
                return 'bg-gray-100 text-gray-600';
        }
    };

    const getMonthName = (month: number) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[month];
    };

    // Filter employees based on search term
    const filteredEmployees = employees.filter(employee => {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
        const employeeId = (employee.type === 'staff' ? employee.staffId : employee.teacherId)?.toLowerCase() || '';
        const designation = employee.currentPosition?.designation?.toLowerCase() || '';
        const department = employee.currentPosition?.department?.toLowerCase() || '';

        return fullName.includes(searchLower) ||
            employeeId.includes(searchLower) ||
            designation.includes(searchLower) ||
            department.includes(searchLower);
    });

    const getAttendanceSummary = () => {
        const today = new Date().toISOString().split('T')[0];
        const todayDate = new Date();
        const isTodayHoliday = isHoliday(todayDate);

        const present = filteredEmployees.filter(emp => emp.attendance[today] === 'present').length;
        const absent = filteredEmployees.filter(emp => emp.attendance[today] === 'absent').length;
        const leave = filteredEmployees.filter(emp => emp.attendance[today] === 'leave').length;
        const holidays = isTodayHoliday ? filteredEmployees.length : 0;
        const notMarked = filteredEmployees.length - present - absent - leave - holidays;

        return { totalEmployees: filteredEmployees.length, present, absent, leave, holidays, notMarked };
    };

    const summary = getAttendanceSummary();

    // Pagination logic
    const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, itemsPerPage]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg">
                        <Calendar className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Bulk Attendance Manager</h2>
                        <p className="text-gray-600">Manage attendance for all {selectedEmployeeType}s</p>
                    </div>
                </div>
                <Button variant="secondary" onClick={onBack}>
                    Back
                </Button>
            </div>

            {/* Month Navigation and Filters */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="secondary"
                                onClick={() => navigateMonth('prev')}
                                className="p-2"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>

                            <div className="text-center">
                                <h3 className="text-xl font-semibold text-gray-900">
                                    {getMonthName(currentMonth)} {currentYear}
                                </h3>
                            </div>

                            <Button
                                variant="secondary"
                                onClick={() => navigateMonth('next')}
                                className="p-2"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Filter className="h-4 w-4 text-gray-500" />
                                <label className="text-sm font-medium text-gray-700">Type:</label>
                                <select
                                    value={selectedEmployeeType}
                                    onChange={(e) => setSelectedEmployeeType(e.target.value as EmployeeType)}
                                    className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                >
                                    <option value="staff">Staff</option>
                                    <option value="teacher">Teachers</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Attendance Summary */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Today's Attendance Summary {searchTerm && `(${filteredEmployees.length} filtered)`}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900">{summary.totalEmployees}</div>
                            <div className="text-sm text-gray-600">Total</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{summary.present}</div>
                            <div className="text-sm text-gray-600">Present</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">{summary.absent}</div>
                            <div className="text-sm text-gray-600">Absent</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{summary.leave}</div>
                            <div className="text-sm text-gray-600">Leave</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-orange-600">{summary.holidays}</div>
                            <div className="text-sm text-gray-600">Holiday</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-gray-600">{summary.notMarked}</div>
                            <div className="text-sm text-gray-600">Not Marked</div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Attendance Table */}
            <Card glassy={true} className="islamic-pattern overflow-hidden">
                <div className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 space-y-4 lg:space-y-0">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {selectedEmployeeType.charAt(0).toUpperCase() + selectedEmployeeType.slice(1)} Attendance - {getMonthName(currentMonth)} {currentYear}
                            </h3>
                            <div className="text-sm text-gray-600">
                                {filteredEmployees.length} of {employees.length} {selectedEmployeeType}s
                            </div>
                        </div>

                        {/* Search Field */}
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search by name, ID, designation, or department..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 w-full sm:w-80"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                                <p className="text-sm text-gray-600">Loading attendance data...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Employee
                                        </th>
                                        {monthDates.map((dateInfo) => (
                                            <th
                                                key={dateInfo.dateStr}
                                                className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${dateInfo.isFuture ? 'text-gray-400' : 'text-gray-500'
                                                    }`}
                                            >
                                                <div className="flex flex-col items-center">
                                                    <span>{dateInfo.day}</span>
                                                    {dateInfo.isToday && (
                                                        <span className="text-xs text-emerald-600">Today</span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedEmployees.map((employee, index) => (
                                        <tr key={employee.id} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                                                        <span className="text-white font-semibold text-xs">
                                                            {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {employee.firstName} {employee.lastName}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {employee.type === 'staff'
                                                                ? employee.staffId
                                                                : employee.teacherId}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            {monthDates.map((dateInfo) => {
                                                const status = employee.attendance[dateInfo.dateStr] || (dateInfo.isHoliday ? 'holiday' : null);
                                                const isFuture = dateInfo.isFuture;
                                                const isHolidayDay = dateInfo.isHoliday;

                                                return (
                                                    <td key={dateInfo.dateStr} className="px-2 py-4 text-center">
                                                        <button
                                                            onClick={() => handleDateClick(employee, dateInfo.dateStr)}
                                                            disabled={isFuture || isHolidayDay}
                                                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isFuture || isHolidayDay
                                                                ? 'bg-gray-100 cursor-not-allowed opacity-50'
                                                                : status && status !== 'holiday'
                                                                    ? `${getStatusColor(status)} cursor-pointer hover:opacity-80`
                                                                    : 'bg-gray-100 hover:bg-gray-200 cursor-pointer'
                                                                } ${isHolidayDay ? 'bg-orange-100' : ''}`}
                                                        >
                                                            {getStatusIcon(status)}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && filteredEmployees.length > 0 && (
                        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                    <label className="text-sm font-medium text-gray-700">Show:</label>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                        className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    >
                                        <option value={10}>10</option>
                                        <option value={15}>15</option>
                                        <option value={20}>20</option>
                                    </select>
                                    <span className="text-sm text-gray-600">per page</span>
                                </div>

                                <div className="text-sm text-gray-600">
                                    Showing {startIndex + 1} to {Math.min(endIndex, filteredEmployees.length)} of {filteredEmployees.length} entries
                                </div>
                            </div>

                            {totalPages > 1 && (
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="secondary"
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2"
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>

                                    <div className="flex items-center space-x-1">
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum: number;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${currentPage === pageNum
                                                        ? 'bg-emerald-600 text-white'
                                                        : 'text-gray-600 hover:bg-gray-100'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <Button
                                        variant="secondary"
                                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2"
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Card>

            {/* Status Selection Modal */}
            {showStatusModal && targetEmployee && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowStatusModal(false)}
                    title={`Mark Attendance - ${targetEmployee.firstName} ${targetEmployee.lastName}`}
                    size="sm"
                >
                    <div className="space-y-6">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Select Attendance Status
                            </h3>
                            <p className="text-sm text-gray-600">
                                {targetEmployee.firstName} {targetEmployee.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                                {new Date(targetDate).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                        </div>

                        <div className="space-y-3">
                            {[
                                { value: 'present', label: 'Present', icon: CheckCircle, color: 'text-green-600' },
                                { value: 'absent', label: 'Absent', icon: XCircle, color: 'text-red-600' },
                                { value: 'leave', label: 'Leave', icon: AlertCircle, color: 'text-blue-600' }
                            ].map((status) => {
                                const Icon = status.icon;
                                const isSelected = bulkStatus === status.value;

                                return (
                                    <button
                                        key={status.value}
                                        onClick={() => setBulkStatus(status.value as AttendanceStatus)}
                                        className={`w-full p-4 border-2 rounded-lg transition-all duration-200 ${isSelected
                                            ? 'border-emerald-500 bg-emerald-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <Icon className={`h-6 w-6 ${status.color}`} />
                                            <span className="font-medium text-gray-900">{status.label}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                variant="secondary"
                                onClick={() => setShowStatusModal(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleBulkStatusSave}
                                disabled={bulkStatus === null || saving}
                                loading={saving}
                            >
                                {saving ? 'Saving...' : 'Save Attendance'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default BulkAttendanceManager;
